import type {
  IVirtualizedTreeNode,
  IVirtualizedTreeNodeModel,
} from "#/ui-kit/components/ui/virtualized-tree/virtualized-tree.types";

import { sliceTask } from "@voltalis/frontend-utils";
import { isEqual } from "es-toolkit";
import { markRaw } from "vue";

export function isNodeParent<T>(node: IVirtualizedTreeNode<T> | IVirtualizedTreeNodeModel<T>): boolean {
  return !!node.children?.length;
}

export function isNodeExpanded<T>(node: IVirtualizedTreeNode<T> | IVirtualizedTreeNodeModel<T>): boolean {
  return !!node.state?.isExpanded;
}

/**
 *
 * @param nodes - tree nodes
 * @param parentsIndexes - specify a node parents indexes to get that node & its children flattened tree
 * @returns the tree nodes or a portion of that tree in a flattened format
 */
export async function getFlattenedTree<
  T,
  NODE extends IVirtualizedTreeNode<T> & { children: NODE[] },
  NODE_MODEL extends IVirtualizedTreeNodeModel<T> & { children: NODE_MODEL[] },
>(nodes: Array<NODE | NODE_MODEL>, parentsIndexes: Array<number> = []): Promise<Array<NODE_MODEL>> {
  /**
   * Reactive object makes the iteration time-consuming.
   * To avoid expensive traversal, we have to markRaw object.
   */
  const flattenedTree = markRaw<Array<NODE_MODEL>>([]);

  // Make sure node pushed to the flattened tree is not reactive.
  function addNodeToFlattenedTree(node: NODE_MODEL) {
    flattenedTree.push(markRaw<NODE_MODEL>(node));
  }

  function shouldTraverse(node: NODE_MODEL): boolean {
    return isNodeParent(node) && isNodeExpanded(node);
  }

  await traverse(nodes, parentsIndexes, addNodeToFlattenedTree, shouldTraverse);

  return flattenedTree;
}

/**
 * build a named function for traverse() method to leverage
 * to avoid creating multiple anonymous functions
 * which is expensive
 */
function constructBfsTraverseStack<
  T,
  NODE extends IVirtualizedTreeNode<T> & { children: NODE[] },
  NODE_MODEL extends IVirtualizedTreeNodeModel<T> & { children: NODE_MODEL[] },
>(nodes: Array<NODE | NODE_MODEL>, parentsIndexes: Array<number> = []): Array<NODE_MODEL> {
  return nodes.map((n, index) => {
    const node = <NODE_MODEL>n;
    return {
      ...node,
      key: parentsIndexes.concat(index).toString(),
      parentsIndexes,
      index: index,
      children: node.children ?? [],
      state: {
        ...node.state,
        isExpanded: !!isNodeExpanded(node),
        isLeaf: !isNodeParent(node),
      },
    };
  });
}

/**
 * use iteration to flatten tree structure to one dimension for virtualised list
 * use recursive method might cause stack overflow exception
 */
async function traverse<
  T,
  NODE extends IVirtualizedTreeNode<T> & { children: NODE[] },
  NODE_MODEL extends IVirtualizedTreeNodeModel<T> & { children: NODE_MODEL[] },
>(
  nodes: Array<NODE | NODE_MODEL>,
  parentsIndexes: Array<number> = [],
  cb: (node: NODE_MODEL) => any,
  shouldTraverse: (node: NODE_MODEL) => boolean,
  useTimeSlicing = true,
): Promise<void> {
  let stack = constructBfsTraverseStack<T, NODE, NODE_MODEL>(nodes, parentsIndexes);
  let i = 1;

  while (stack.length > 0) {
    const node = stack.shift();
    if (node === undefined) break;
    cb(node);
    if (shouldTraverse(node)) {
      const builtTraverseStack = constructBfsTraverseStack<T, NODE, NODE_MODEL>(
        ((<NODE_MODEL>node).children as NODE_MODEL[]) ?? [],
        (<NODE_MODEL>node).parentsIndexes.concat((<NODE_MODEL>node).index),
      );

      stack = [...builtTraverseStack, ...stack];
    }

    if (useTimeSlicing) await sliceTask(i++, 1000, 1);
  }
}

function areTabsDifferents(tab1: number[], tab2: number[]): boolean {
  if (tab1.length !== tab2.length) return true;

  for (let i = 0; i < tab1.length; i++) {
    if (tab1[i] !== tab2[i]) return true;
  }

  return false;
}

// count the amount of visible descendants based on the node
export async function getNumberOfVisibleDescendants<
  T,
  NODE_MODEL extends IVirtualizedTreeNodeModel<T> & { children: NODE_MODEL[] },
>(node: NODE_MODEL, flattenedTree: Array<NODE_MODEL>, useTimeSlicing: boolean = true): Promise<number> {
  const nodePath = [...node.parentsIndexes, node.index];
  const nodeIndexInFlattenedTree = getNodeIndexInFlattenedTree(node, flattenedTree);
  let count = 0;

  for (let i = nodeIndexInFlattenedTree + 1; i < flattenedTree.length; i++) {
    const currentNode = flattenedTree[i];
    if (!currentNode || areTabsDifferents(currentNode.parentsIndexes.slice(0, nodePath.length), nodePath)) break;
    else count++;

    if (useTimeSlicing) await sliceTask(i, 1000, 1);
  }

  return count;
}

export function getNodeIndexInFlattenedTree<
  T,
  NODE_MODEL extends IVirtualizedTreeNodeModel<T> & { children: NODE_MODEL[] },
>(node: NODE_MODEL, flattenedTree: NODE_MODEL[]): number {
  return flattenedTree.findIndex((n) => isEqual([...n.parentsIndexes, n.index], [...node.parentsIndexes, node.index]));
}

export function isNodeChild<T, NODE_MODEL extends IVirtualizedTreeNodeModel<T> & { children: NODE_MODEL[] }>(
  node: NODE_MODEL,
  maybeParentNode: NODE_MODEL,
): boolean {
  const nodeParentsIndexes = node.parentsIndexes.slice(0, maybeParentNode.parentsIndexes.length + 1);
  const maybeParentPath = [...maybeParentNode.parentsIndexes, maybeParentNode.index];
  return isEqual(maybeParentPath, nodeParentsIndexes);
}
